'use client';

import { c as _c } from "react/compiler-runtime";
import { ReactNode, createContext, useContext, useEffect, useState, type CSSProperties } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { useMediaQuery, BREAKPOINT_QUERIES } from '@/lib/hooks/use-media-query';
import { RAIL_STRIP_WIDTH } from '@/components/ui/rail';
interface ShellProps {
  header?: ReactNode;
  rail?: ReactNode;
  sidebar?: ReactNode;
  sidebarStack?: ReactNode;
  aside?: ReactNode;
  children: ReactNode;
  sidebarOpen?: boolean;
  sidebarWidth?: number;
  asideOpen?: boolean;
  asideWidth?: number;
  asideResizing?: boolean;
  onAsideClose?: () => void;
  variant?: 'workspace' | 'hub';
  mainId?: string;
  skipLinkLabel?: string;
  className?: string;
}

/** Shell size templates — consumers should prefer these over one-off widths. */
export const DEFAULT_SIDEBAR_WIDTH = 320;
export const DEFAULT_ASIDE_WIDTH = 360;
/** Chat collapse strip — matches ChatPanel / useShellChatAside. */
export const COLLAPSED_ASIDE_WIDTH = 48;
const PANEL_CLASSES = 'bg-surface-1 rounded-lg overflow-hidden';

/**
 * How the sidebar is presented at the current width, published so the header
 * can render a trigger. Below `narrow` (1024) the sidebar is an overlay drawer
 * rather than a column: a persistent 320px sidebar on a 768 portrait tablet
 * left the main panel 328px wide, narrower than a phone, which clipped its
 * own content. The aside has behaved this way below `wide` since it was
 * written; this gives the sidebar the matching treatment one step earlier.
 */
export interface ShellSidebarControl {
  /** True when the sidebar is an overlay rather than a column. */
  isDrawer: boolean;
  /** True when there is a sidebar to show at all. */
  hasSidebar: boolean;
  /** True when the header should offer a control (drawer, or collapsible column). */
  canToggle: boolean;
  /** True when the sidebar is currently hidden — drawer shut, or column collapsed. */
  isHidden: boolean;
  /** Open the drawer, or expand/collapse the column. */
  toggle: () => void;
}
const ShellSidebarContext = createContext<ShellSidebarControl>({
  isDrawer: false,
  hasSidebar: false,
  canToggle: false,
  isHidden: false,
  toggle: () => {}
});

/** Read by the workspace header to decide whether to offer a sidebar trigger. */
export function useShellSidebar() {
  return useContext(ShellSidebarContext);
}
export function Shell(t0) {
  const $ = _c(83);
  const {
    header,
    rail,
    sidebar,
    sidebarStack,
    aside,
    children,
    sidebarOpen: t1,
    sidebarWidth: t2,
    asideOpen: t3,
    asideWidth: t4,
    asideResizing: t5,
    onAsideClose,
    variant: t6,
    mainId: t7,
    skipLinkLabel: t8,
    className
  } = t0;
  const sidebarOpen = t1 === undefined ? true : t1;
  const sidebarWidth = t2 === undefined ? DEFAULT_SIDEBAR_WIDTH : t2;
  const asideOpen = t3 === undefined ? true : t3;
  const asideWidth = t4 === undefined ? DEFAULT_ASIDE_WIDTH : t4;
  const asideResizing = t5 === undefined ? false : t5;
  const variant = t6 === undefined ? "workspace" : t6;
  const mainId = t7 === undefined ? "main-content" : t7;
  const skipLinkLabel = t8 === undefined ? "Skip to main content" : t8;
  const isHub = variant === "hub";
  const isDrawerMode = useMediaQuery(BREAKPOINT_QUERIES.isBelowWide);
  const isSidebarDrawer = useMediaQuery(BREAKPOINT_QUERIES.isNarrow);
  const reduce = useReducedMotion();
  const hasSidebar = !isHub && sidebar !== undefined && sidebarOpen;
  const [sidebarDrawerOpen, setSidebarDrawerOpen] = useState(false);
  const isSidebarCollapsible = useMediaQuery(BREAKPOINT_QUERIES.isCompact);
  const [sidebarColumnCollapsed, setSidebarColumnCollapsed] = useState(false);
  const columnVisible = sidebarOpen && !(isSidebarCollapsible && sidebarColumnCollapsed);
  const [wasSidebarDrawer, setWasSidebarDrawer] = useState(isSidebarDrawer);
  if (wasSidebarDrawer !== isSidebarDrawer) {
    setWasSidebarDrawer(isSidebarDrawer);
    if (!isSidebarDrawer) {
      setSidebarDrawerOpen(false);
    }
  }
  let t10;
  let t9;
  if ($[0] !== sidebarDrawerOpen) {
    t9 = () => {
      if (!sidebarDrawerOpen) {
        return;
      }
      const handler = e => {
        if (e.key === "Escape") {
          setSidebarDrawerOpen(false);
        }
      };
      window.addEventListener("keydown", handler);
      return () => window.removeEventListener("keydown", handler);
    };
    t10 = [sidebarDrawerOpen];
    $[0] = sidebarDrawerOpen;
    $[1] = t10;
    $[2] = t9;
  } else {
    t10 = $[1];
    t9 = $[2];
  }
  useEffect(t9, t10);
  const asideDrawerExpanded = isDrawerMode && asideOpen && asideWidth > COLLAPSED_ASIDE_WIDTH;
  let t11;
  let t12;
  if ($[3] !== asideDrawerExpanded || $[4] !== onAsideClose) {
    t11 = () => {
      if (!asideDrawerExpanded || !onAsideClose) {
        return;
      }
      const handler_0 = e_0 => {
        if (e_0.key === "Escape") {
          onAsideClose();
        }
      };
      window.addEventListener("keydown", handler_0);
      return () => window.removeEventListener("keydown", handler_0);
    };
    t12 = [asideDrawerExpanded, onAsideClose];
    $[3] = asideDrawerExpanded;
    $[4] = onAsideClose;
    $[5] = t11;
    $[6] = t12;
  } else {
    t11 = $[5];
    t12 = $[6];
  }
  useEffect(t11, t12);
  const drawerSidebarWidth = Math.min(sidebarWidth, typeof window !== "undefined" ? 0.85 * window.innerWidth : sidebarWidth);
  let t13;
  if ($[7] !== sidebar || $[8] !== sidebarOpen || $[9] !== sidebarStack) {
    t13 = width => sidebarStack ? <div style={{
      width
    }} className="h-full flex flex-col gap-3"><div className={cn(PANEL_CLASSES, "flex-1 min-h-0")}>{sidebar}</div>{sidebarStack}</div> : <div style={{
      width
    }} className={cn("h-full", sidebarOpen && "bg-surface-1 rounded-lg")}>{sidebar}</div>;
    $[7] = sidebar;
    $[8] = sidebarOpen;
    $[9] = sidebarStack;
    $[10] = t13;
  } else {
    t13 = $[10];
  }
  const sidebarBody = t13;
  const t14 = hasSidebar && (isSidebarDrawer || isSidebarCollapsible);
  const t15 = isSidebarDrawer ? !sidebarDrawerOpen : !columnVisible;
  let t16;
  if ($[11] !== isSidebarDrawer) {
    t16 = () => isSidebarDrawer ? setSidebarDrawerOpen(_temp) : setSidebarColumnCollapsed(_temp2);
    $[11] = isSidebarDrawer;
    $[12] = t16;
  } else {
    t16 = $[12];
  }
  let t17;
  if ($[13] !== hasSidebar || $[14] !== isSidebarDrawer || $[15] !== t14 || $[16] !== t15 || $[17] !== t16) {
    t17 = {
      isDrawer: isSidebarDrawer,
      hasSidebar,
      canToggle: t14,
      isHidden: t15,
      toggle: t16
    };
    $[13] = hasSidebar;
    $[14] = isSidebarDrawer;
    $[15] = t14;
    $[16] = t15;
    $[17] = t16;
    $[18] = t17;
  } else {
    t17 = $[18];
  }
  let t18;
  if ($[19] !== className) {
    t18 = cn("flex h-screen w-full max-w-full flex-col overflow-x-hidden bg-surface-0 p-0 md:p-3", className);
    $[19] = className;
    $[20] = t18;
  } else {
    t18 = $[20];
  }
  let t19;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = {
      "--shell-rail-width": `${RAIL_STRIP_WIDTH}px`
    };
    $[21] = t19;
  } else {
    t19 = $[21];
  }
  const t20 = `#${mainId}`;
  let t21;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = cn("sr-only focus:not-sr-only", "focus:fixed focus:top-2 focus:left-2 focus:z-50", "focus:px-4 focus:py-2 focus:rounded-md", "focus:bg-surface-3 focus:text-[var(--text-primary)] focus:text-label", "focus:ring-2 focus:ring-ds-accent");
    $[22] = t21;
  } else {
    t21 = $[22];
  }
  let t22;
  if ($[23] !== skipLinkLabel || $[24] !== t20) {
    t22 = <a href={t20} className={t21}>{skipLinkLabel}</a>;
    $[23] = skipLinkLabel;
    $[24] = t20;
    $[25] = t22;
  } else {
    t22 = $[25];
  }
  let t23;
  if ($[26] !== header) {
    t23 = header && <div className="shrink-0">{header}</div>;
    $[26] = header;
    $[27] = t23;
  } else {
    t23 = $[27];
  }
  let t24;
  if ($[28] !== isHub || $[29] !== rail) {
    t24 = !isHub && rail && <div className={cn(PANEL_CLASSES, "hidden shrink-0 md:block")}>{rail}</div>;
    $[28] = isHub;
    $[29] = rail;
    $[30] = t24;
  } else {
    t24 = $[30];
  }
  let t25;
  if ($[31] !== columnVisible || $[32] !== isHub || $[33] !== isSidebarDrawer || $[34] !== sidebar || $[35] !== sidebarBody || $[36] !== sidebarWidth) {
    t25 = !isHub && sidebar !== undefined && !isSidebarDrawer && <div aria-hidden={!columnVisible} className={cn("shrink-0 overflow-hidden min-h-0", "transition-[width] duration-[var(--duration-emphasis)] ease-[var(--ease-out-expo)]", "motion-reduce:transition-none")} style={{
      width: columnVisible ? sidebarWidth : 0
    }}>{sidebarBody(sidebarWidth)}</div>;
    $[31] = columnVisible;
    $[32] = isHub;
    $[33] = isSidebarDrawer;
    $[34] = sidebar;
    $[35] = sidebarBody;
    $[36] = sidebarWidth;
    $[37] = t25;
  } else {
    t25 = $[37];
  }
  const t26 = isDrawerMode && aside !== undefined && asideOpen && "pr-14";
  let t27;
  if ($[38] !== t26) {
    t27 = cn(PANEL_CLASSES, "flex flex-col flex-1 min-w-0 min-h-0", t26);
    $[38] = t26;
    $[39] = t27;
  } else {
    t27 = $[39];
  }
  let t28;
  if ($[40] !== children || $[41] !== mainId || $[42] !== t27) {
    t28 = <main id={mainId} className={t27}>{children}</main>;
    $[40] = children;
    $[41] = mainId;
    $[42] = t27;
    $[43] = t28;
  } else {
    t28 = $[43];
  }
  let t29;
  if ($[44] !== aside || $[45] !== asideOpen || $[46] !== asideResizing || $[47] !== asideWidth || $[48] !== isDrawerMode || $[49] !== isHub) {
    t29 = !isHub && aside !== undefined && !isDrawerMode && <div aria-hidden={!asideOpen} className={cn("shrink-0 overflow-hidden", !asideResizing && "transition-[width] duration-[var(--duration-emphasis)] ease-[var(--ease-out-expo)]", "motion-reduce:transition-none", asideOpen && "bg-surface-1 rounded-lg")} style={{
      width: asideOpen ? asideWidth : 0
    }}><div style={{
        width: asideWidth
      }} className="h-full">{aside}</div></div>;
    $[44] = aside;
    $[45] = asideOpen;
    $[46] = asideResizing;
    $[47] = asideWidth;
    $[48] = isDrawerMode;
    $[49] = isHub;
    $[50] = t29;
  } else {
    t29 = $[50];
  }
  let t30;
  if ($[51] !== t24 || $[52] !== t25 || $[53] !== t28 || $[54] !== t29) {
    t30 = <div className="mt-0 flex min-h-0 flex-1 gap-0 md:mt-3 md:gap-3">{t24}{t25}{t28}{t29}</div>;
    $[51] = t24;
    $[52] = t25;
    $[53] = t28;
    $[54] = t29;
    $[55] = t30;
  } else {
    t30 = $[55];
  }
  let t31;
  if ($[56] !== aside || $[57] !== asideDrawerExpanded || $[58] !== asideOpen || $[59] !== asideWidth || $[60] !== isDrawerMode || $[61] !== isHub || $[62] !== onAsideClose || $[63] !== reduce) {
    t31 = !isHub && aside !== undefined && isDrawerMode && asideOpen && <AnimatePresence>{asideDrawerExpanded ? <><motion.div key="aside-backdrop" className="fixed inset-0 z-40 bg-black/50" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={reduce ? {
          duration: 0
        } : {
          duration: 0.2,
          ease: "easeOut"
        }} onClick={onAsideClose} aria-hidden="true" /><motion.div key="aside-drawer" role="dialog" aria-modal="true" aria-label="Assistant" className={cn(PANEL_CLASSES, "fixed z-50 top-3 bottom-3 right-3")} style={{
          width: Math.min(asideWidth, typeof window !== "undefined" ? 0.9 * window.innerWidth : asideWidth)
        }} initial={{
          x: "100%"
        }} animate={{
          x: 0
        }} exit={{
          x: "100%"
        }} transition={reduce ? {
          duration: 0
        } : {
          type: "spring",
          stiffness: 320,
          damping: 32,
          mass: 0.8
        }}>{aside}</motion.div></> : <div key="aside-collapsed-strip" className={cn(PANEL_CLASSES, "fixed z-50 top-3 bottom-3 right-3")} style={{
        width: COLLAPSED_ASIDE_WIDTH
      }}>{aside}</div>}</AnimatePresence>;
    $[56] = aside;
    $[57] = asideDrawerExpanded;
    $[58] = asideOpen;
    $[59] = asideWidth;
    $[60] = isDrawerMode;
    $[61] = isHub;
    $[62] = onAsideClose;
    $[63] = reduce;
    $[64] = t31;
  } else {
    t31 = $[64];
  }
  let t32;
  if ($[65] !== drawerSidebarWidth || $[66] !== isHub || $[67] !== isSidebarDrawer || $[68] !== reduce || $[69] !== sidebar || $[70] !== sidebarBody || $[71] !== sidebarDrawerOpen) {
    t32 = !isHub && sidebar !== undefined && isSidebarDrawer && <AnimatePresence>{sidebarDrawerOpen && <><motion.div key="sidebar-backdrop" className="fixed inset-0 z-40 bg-black/50" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={reduce ? {
          duration: 0
        } : {
          duration: 0.2,
          ease: "easeOut"
        }} onClick={() => setSidebarDrawerOpen(false)} aria-hidden="true" /><motion.div key="sidebar-drawer" role="dialog" aria-modal="true" aria-label="Workspace navigation" className="fixed z-50 top-3 bottom-3 left-3" style={{
          width: drawerSidebarWidth
        }} initial={{
          x: "-100%"
        }} animate={{
          x: 0
        }} exit={{
          x: "-100%"
        }} transition={reduce ? {
          duration: 0
        } : {
          type: "spring",
          stiffness: 320,
          damping: 32,
          mass: 0.8
        }}>{sidebarBody(drawerSidebarWidth)}</motion.div></>}</AnimatePresence>;
    $[65] = drawerSidebarWidth;
    $[66] = isHub;
    $[67] = isSidebarDrawer;
    $[68] = reduce;
    $[69] = sidebar;
    $[70] = sidebarBody;
    $[71] = sidebarDrawerOpen;
    $[72] = t32;
  } else {
    t32 = $[72];
  }
  let t33;
  if ($[73] !== t18 || $[74] !== t22 || $[75] !== t23 || $[76] !== t30 || $[77] !== t31 || $[78] !== t32) {
    t33 = <div data-workspace-shell={true} className={t18} style={t19 as CSSProperties}>{t22}{t23}{t30}{t31}{t32}</div>;
    $[73] = t18;
    $[74] = t22;
    $[75] = t23;
    $[76] = t30;
    $[77] = t31;
    $[78] = t32;
    $[79] = t33;
  } else {
    t33 = $[79];
  }
  let t34;
  if ($[80] !== t17 || $[81] !== t33) {
    t34 = <ShellSidebarContext.Provider value={t17}>{t33}</ShellSidebarContext.Provider>;
    $[80] = t17;
    $[81] = t33;
    $[82] = t34;
  } else {
    t34 = $[82];
  }
  return t34;
}
function _temp2(c) {
  return !c;
}
function _temp(o) {
  return !o;
}